/**
 * Who a dashboard is shared with, and changing that.
 *
 * The catalog's share dialog, over dashboard grants - deliberately the same
 * screen, down to the class names, because it is the same act and people should
 * not have to learn it twice. One box to add someone, with the role and an
 * optional end date beside it, then everyone who has access, each with a role
 * menu: Viewer, Editor, or Remove access.
 *
 * What is missing from it is the **Public** switch. A chart shared with
 * everyone lands on a shelf people browse; dashboards have no such shelf, so an
 * everyone grant would only mean "in the list of every account in the fleet,
 * forever" - which is not what anyone means by clicking Share. The API refuses
 * one as well, rather than leaving it to this file.
 *
 * `admin` is not offered here either, and the database would refuse it anyway
 * unless the actor is root: sharing a dashboard is not how anyone should
 * acquire authority over the app.
 */

import { html, nothing, svg, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import type { DashboardGrant, PrincipalSearch } from '../../api/types.ts';
import { LightElement } from '../../lit-base.ts';
import { StoreController } from '../../state/controller.ts';
import { showToast } from '../../ui/toast.ts';
import {
  changeDashboardShareRole,
  closeDashboardShareDialog,
  dashboardGrants,
  dashboardGrantsLoading,
  findDashboardPrincipals,
  revokeDashboardShare,
  shareDashboard,
  sharingDashboard,
  sharingDashboardId,
} from './dashboard-share-state.ts';
import { chartTiles, dashboards } from './dashboard-state.ts';

type Role = 'viewer' | 'editor';

const EMPTY: PrincipalSearch = { users: [], groups: [] };

const ROLE_LABEL: Record<Role, string> = { viewer: 'Viewer', editor: 'Editor' };
const ROLE_HINT: Record<Role, string> = {
  viewer: 'Can open and read it',
  editor: 'Can also change and share it',
};

const DASHBOARD_GLYPH = svg`<svg
  class="sd-thumb-glyph"
  width="20"
  height="20"
  viewBox="0 0 24 24"
  fill="none"
  aria-hidden="true"
>
  <rect x="4" y="3.5" width="16" height="17" rx="1" stroke="currentColor" stroke-width="1.6" />
  <path d="M8 8h8M8 12h8M8 16h5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" />
</svg>`;

const CLOCK = svg`<svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
  <circle cx="12" cy="12" r="8.5" stroke="currentColor" stroke-width="1.8" />
  <path d="M12 7.5V12l3 2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
</svg>`;

const LINK = svg`<svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
  <path
    d="M10 14a4.5 4.5 0 0 0 6.4 0l3-3a4.5 4.5 0 0 0-6.4-6.4l-1 1M14 10a4.5 4.5 0 0 0-6.4 0l-3 3a4.5 4.5 0 0 0 6.4 6.4l1-1"
    stroke="currentColor"
    stroke-width="1.8"
    stroke-linecap="round"
  />
</svg>`;

const CLOSE = svg`<svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
  <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
</svg>`;

/** "Maria Keller" → "MK". Two letters read at avatar size; three do not. */
function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('');
}

function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

@customElement('dashboard-share-dialog')
export class DashboardShareDialog extends LightElement {
  private store = new StoreController(this, [
    sharingDashboardId,
    dashboardGrants,
    dashboardGrantsLoading,
    dashboards,
  ]);

  @state() private query = '';
  @state() private found: PrincipalSearch = EMPTY;
  // Not `role`: that is a property of HTMLElement, and shadowing it breaks the
  // element's own typing.
  @state() private accessLevel: Role = 'viewer';
  @state() private useExpiry = false;
  @state() private expiresAt = '';
  @state() private busy = false;
  /** Which menu is open: the add box's role picker, or one grant's. */
  @state() private menu: 'add' | string | null = null;
  /**
   * Where that menu sits, relative to the dialog. Placed on the dialog rather
   * than inside the people list because the list scrolls, and a menu inside a
   * scroller is clipped by it.
   */
  @state() private menuAt: { x: number; y: number } | null = null;
  @state() private copied = false;

  private searchTimer: number | null = null;

  override connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener('keydown', this.onKey);
    document.addEventListener('click', this.onDocClick);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this.onKey);
    document.removeEventListener('click', this.onDocClick);
  }

  /** Escape closes the open menu first, then the dialog. */
  private onKey = (e: KeyboardEvent): void => {
    if (e.key !== 'Escape' || !sharingDashboardId.value) return;
    if (this.menu) this.menu = null;
    else this.close();
  };

  private onDocClick = (): void => {
    if (this.menu) this.menu = null;
  };

  /** Open a role menu under its button, flipping up when there is no room below. */
  private openMenu(key: string, e: Event): void {
    e.stopPropagation();
    if (this.menu === key) {
      this.menu = null;
      return;
    }
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const dialog = this.querySelector('.sd')?.getBoundingClientRect();
    if (!dialog) return;
    const width = 208;
    const height = key === 'add' ? 110 : 150;
    const below = r.bottom + 3 + height <= window.innerHeight;
    this.menuAt = {
      x: Math.max(8, r.right - width) - dialog.left,
      y: (below ? r.bottom + 3 : r.top - 3 - height) - dialog.top,
    };
    this.menu = key;
  }

  private close(): void {
    this.query = '';
    this.found = EMPTY;
    this.menu = null;
    this.useExpiry = false;
    this.expiresAt = '';
    this.accessLevel = 'viewer';
    closeDashboardShareDialog();
  }

  private onSearchInput(value: string): void {
    this.query = value;
    if (this.searchTimer) window.clearTimeout(this.searchTimer);
    if (!value.trim()) {
      this.found = EMPTY;
      return;
    }
    // Debounced: the picker searches every user in the directory, and a
    // keystroke is not a question worth asking the database.
    this.searchTimer = window.setTimeout(async () => {
      this.found = await findDashboardPrincipals(value.trim());
    }, 250);
  }

  private async grant(principal: { user_id?: string; group_id?: string }): Promise<void> {
    const id = sharingDashboardId.value;
    if (!id || this.busy) return;
    this.busy = true;
    const expiry = this.useExpiry && this.expiresAt ? new Date(this.expiresAt).toISOString() : null;
    await shareDashboard(id, principal, this.accessLevel, expiry);
    this.busy = false;
    this.query = '';
    this.found = EMPTY;
  }

  private async run(work: () => Promise<unknown>): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    this.menu = null;
    await work();
    this.busy = false;
  }

  private async copyLink(id: string): Promise<void> {
    // The same link the browse list produces when a dashboard is open, so what
    // is pasted into a message is the door the person already knows.
    const url = `${window.location.origin}/?section=dashboards&db=${id}`;
    try {
      await navigator.clipboard.writeText(url);
      this.copied = true;
      setTimeout(() => {
        this.copied = false;
      }, 1400);
    } catch {
      showToast('error', 'Copy failed - the link is ' + url);
    }
  }

  /* ── pieces ─────────────────────────────────────────────── */

  private renderRoleMenu(current: Role, pick: (role: Role) => void, remove?: () => void): TemplateResult {
    const at = this.menuAt ?? { x: 0, y: 0 };
    return html`<div
      class="sd-menu"
      role="menu"
      style=${`left:${at.x}px; top:${at.y}px`}
      @click=${(e: Event) => e.stopPropagation()}
    >
      ${(['viewer', 'editor'] as Role[]).map(
        (role) => html`<button
          class="sd-menu-item ${role === current ? 'is-current' : ''}"
          role="menuitemradio"
          aria-checked=${role === current}
          @click=${() => pick(role)}
        >
          ${ROLE_LABEL[role]}<small>${ROLE_HINT[role]}</small>
        </button>`,
      )}
      ${remove
        ? html`<div class="sd-menu-sep"></div>
            <button class="sd-menu-item is-danger" role="menuitem" @click=${remove}>
              Remove access
            </button>`
        : nothing}
    </div>`;
  }

  private renderCandidates(): TemplateResult | typeof nothing {
    if (!this.query.trim()) return nothing;
    const has = (pid: string) => dashboardGrants.value.some((g) => g.principal_id === pid);
    const people = this.found.users.map((u) => ({
      id: u.id,
      name: u.display_name,
      sub: u.email ?? 'person',
      group: false,
      principal: { user_id: u.id },
    }));
    const groups = this.found.groups.map((g) => ({
      id: g.id,
      name: g.name,
      sub: 'group',
      group: true,
      principal: { group_id: g.id },
    }));
    const all = [...people, ...groups];
    return html`<div class="sd-pop" role="listbox">
      ${all.map(
        (p) => html`<button
          class="sd-candidate"
          role="option"
          ?disabled=${has(p.id) || this.busy}
          @mousedown=${(e: Event) => {
            // Before the input's blur, so the click lands.
            e.preventDefault();
            if (!has(p.id)) void this.grant(p.principal);
          }}
        >
          <span class="sd-avatar ${p.group ? 'is-group' : ''}">${initials(p.name)}</span>
          <span class="sd-who">
            <span class="sd-name">${p.name}</span>
            <span class="sd-sub">${has(p.id) ? 'already has access' : p.sub}</span>
          </span>
          <span class="sd-candidate-role">${has(p.id) ? '' : `Add as ${ROLE_LABEL[this.accessLevel]}`}</span>
        </button>`,
      )}
      ${!all.length ? html`<p class="sd-none">Nobody matches “${this.query}”</p>` : nothing}
    </div>`;
  }

  private renderGrant(grant: DashboardGrant): TemplateResult {
    const role = grant.role as Role;
    const isGroup = grant.principal_kind === 'group';
    return html`<li class="sd-row">
      <span class="sd-avatar ${isGroup ? 'is-group' : ''}">${initials(grant.principal_name)}</span>
      <span class="sd-who">
        <span class="sd-name">${grant.principal_name}</span>
        <span class="sd-sub">
          ${isGroup ? 'group' : (grant.principal_email ?? 'person')}
          ${grant.expires_at ? html` · <span class="sd-until">until ${shortDate(grant.expires_at)}</span>` : nothing}
        </span>
      </span>
      <span class="sd-rolepick">
        <button
          class="sd-role"
          aria-haspopup="menu"
          aria-expanded=${this.menu === grant.id}
          ?disabled=${this.busy}
          @click=${(e: Event) => this.openMenu(grant.id, e)}
        >
          ${ROLE_LABEL[role] ?? grant.role}<span class="sd-caret" aria-hidden="true">▾</span>
        </button>
      </span>
    </li>`;
  }

  /** The one open role menu, drawn on the dialog itself - see `menuAt`. */
  private renderOpenMenu(dashboardId: string): TemplateResult | typeof nothing {
    if (!this.menu) return nothing;
    if (this.menu === 'add') {
      return this.renderRoleMenu(this.accessLevel, (role) => {
        this.accessLevel = role;
        this.menu = null;
      });
    }
    const grant = dashboardGrants.value.find((g) => g.id === this.menu);
    if (!grant) return nothing;
    const role = grant.role as Role;
    return this.renderRoleMenu(
      role,
      (next) =>
        void this.run(async () => {
          if (next !== role) await changeDashboardShareRole(dashboardId, grant.id, next);
        }),
      () => void this.run(() => revokeDashboardShare(dashboardId, grant.id)),
    );
  }

  override render(): TemplateResult | typeof nothing {
    const id = sharingDashboardId.value;
    if (!id) return nothing;
    const dashboard = sharingDashboard();
    if (!dashboard) return nothing;
    const people = dashboardGrants.value.filter((g) => g.principal_kind !== 'everyone');
    const access = dashboard.access;
    // "You" unless the server says otherwise: root and an editor can open this
    // dialog on somebody else's dashboard, and the owner row should say whose.
    const ownerLabel = access?.isOwner === false ? (access.ownerName ?? 'Someone else') : 'You';
    const tiles = dashboard.tabs.reduce((n, t) => n + chartTiles(t).length, 0);
    return html`
      <div class="share-scrim" @click=${() => this.close()}></div>
      <div class="share-dialog sd" role="dialog" aria-modal="true" aria-labelledby="dsd-title">
        <header class="sd-head">
          <span class="sd-thumb">${DASHBOARD_GLYPH}</span>
          <span class="sd-title">
            <span id="dsd-title" class="sd-title-name">${dashboard.name}</span>
            <span class="sd-title-meta">
              ${dashboard.partial
                ? 'Dashboard'
                : `${tiles} ${tiles === 1 ? 'panel' : 'panels'} · ${dashboard.tabs.length} ${
                    dashboard.tabs.length === 1 ? 'tab' : 'tabs'
                  }`}
            </span>
          </span>
          <button class="sd-x" title="Close" aria-label="Close" @click=${() => this.close()}>
            ${CLOSE}
          </button>
        </header>

        <section class="sd-section" aria-label="People and groups">
          <span class="sd-label">People and groups</span>
          <div class="sd-add">
            <input
              type="search"
              placeholder="Add people or groups…"
              aria-label="Add people or groups"
              autocomplete="off"
              .value=${this.query}
              @input=${(e: Event) => this.onSearchInput((e.target as HTMLInputElement).value)}
            />
            <button
              class="sd-add-icon ${this.useExpiry ? 'is-on' : ''}"
              title=${this.useExpiry ? 'No end date' : 'Set an end date for the access you give'}
              aria-label="Access ends"
              aria-pressed=${this.useExpiry}
              @click=${() => {
                this.useExpiry = !this.useExpiry;
              }}
            >
              ${CLOCK}
            </button>
            <span class="sd-rolepick">
              <button
                class="sd-add-role"
                aria-haspopup="menu"
                aria-expanded=${this.menu === 'add'}
                @click=${(e: Event) => this.openMenu('add', e)}
              >
                ${ROLE_LABEL[this.accessLevel]}<span class="sd-caret" aria-hidden="true">▾</span>
              </button>
            </span>
            ${this.renderCandidates()}
          </div>
          ${this.useExpiry
            ? html`<label class="sd-expiry">
                Access ends
                <input
                  type="date"
                  .value=${this.expiresAt}
                  @change=${(e: Event) => {
                    this.expiresAt = (e.target as HTMLInputElement).value;
                  }}
                />
                <span class="sd-expiry-note">for people you add now</span>
              </label>`
            : nothing}

          <ul
            class="sd-rows"
            @scroll=${() => {
              if (this.menu) this.menu = null;
            }}
          >
            <li class="sd-row">
              <span class="sd-avatar">${initials(ownerLabel)}</span>
              <span class="sd-who">
                <span class="sd-name">${ownerLabel}</span>
                <span class="sd-sub">${access?.ownerEmail ?? ''}</span>
              </span>
              <span class="sd-role-static">Owner</span>
            </li>
            ${dashboardGrantsLoading.value
              ? html`<li class="sd-row sd-loading">Loading…</li>`
              : people.map((g) => this.renderGrant(g))}
          </ul>
        </section>

        <footer class="sd-foot">
          <button class="sd-ghost ${this.copied ? 'is-ok' : ''}" @click=${() => void this.copyLink(id)}>
            ${LINK} ${this.copied ? 'Copied' : 'Copy link'}
          </button>
          <button class="catalog-cta" @click=${() => this.close()}>Done</button>
        </footer>
        ${this.renderOpenMenu(id)}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'dashboard-share-dialog': DashboardShareDialog;
  }
}
